// The public program catalog is gym-scoped: it exists only at
// `/programs/<gym-slug>`. There is NO page at a bare `/programs` — that path
// renders the 404 screen. A parent hit exactly that dead end on 2026-07-23 and
// abandoned her enrollment (see PR #2299, which fixed the assistant's copy of
// this bug inside `navigateTo`).
//
// Pages that own a `[gymSlug]` route param already have the slug and can
// interpolate it directly. This helper is for the surfaces that hold only a
// *maybe* slug and therefore have to choose between a real link and no link at
// all. Today every caller reads that maybe-slug from the org context: the web
// sidebar's two "Browse Programs" sites, the registration-complete page, the
// public-catalog hint, and the My Registrations empty state.
// It never returns a slug-less `/programs`, so "no gym" can't silently become
// "404".

// Path-safe slug charset, and it does NOT reject only hypotheticals.
// `20260505_001_ensure_organization_slugs` installs a trigger that NORMALIZES
// slugs toward `[a-z0-9-]`, but normalizing is not validating: its else-branch
// rewrites every non-alphanumeric run to `-` and then strips leading/trailing
// dashes without re-checking for emptiness, so a punctuation-only slug ('###',
// '---', emoji-only) lands in the column as ''. Nothing stops that — the column
// is nullable with no CHECK and no UNIQUE, and cheervue-admin-dashboard's
// org-create route accepts a caller-supplied `body.slug` unvalidated. So `''`
// is a real reachable value that this pattern correctly refuses (see the
// solutions doc for the verified repro). Do not "simplify" this away on the
// theory that the trigger already guarantees the charset.
//
// The set is deliberately a little wider than the trigger's output: still narrow
// enough that path traversal and open redirects are impossible (no `/`, `\`,
// `.`, `%`, whitespace or control characters get through), but an unexpected
// uppercase letter or underscore would still yield a working link instead of
// silently hiding the gym's catalog.
const SAFE_SLUG = /^[A-Za-z0-9_-]+$/;

/**
 * Build the public program catalog path for a gym.
 *
 * @param slug the gym's `organizations.slug`, from anywhere it may be missing
 *             or malformed (the column is nullable and the charset is only
 *             enforced by a write trigger, so validate rather than trust)
 * @returns `/programs/<slug>`, or `null` when there is no usable slug — callers
 *          must handle `null` by hiding the link, never by falling back to
 *          `/programs`.
 */
export function programCatalogPath(slug: string | null | undefined): string | null {
  if (typeof slug !== 'string') return null;

  const trimmed = slug.trim();
  if (!SAFE_SLUG.test(trimmed)) return null;

  return `/programs/${trimmed}`;
}

/**
 * The public detail page for one program.
 *
 * Two shapes, because the routes genuinely differ: private lessons live at
 * `/private-lessons/<id>` (a details page that shows pricing, packages and
 * policies before booking), and everything else at `/<type>/<id>`. Extracted so
 * the catalog card and the crawler summary cannot drift into publishing two
 * different links to the same program.
 *
 * Standalone tryout events are NOT programs and do not belong here — they have
 * their own details page, built by `tryoutDetailPath` below.
 *
 * @returns the path, or `null` when the gym slug is not one we can build a link
 *          from — callers must hide the link rather than fall back.
 */
export function programDetailPath(
  gymSlug: string | null | undefined,
  programType: string | null | undefined,
  programId: string | null | undefined
): string | null {
  const base = programCatalogPath(gymSlug);
  if (!base) return null;
  if (typeof programId !== 'string' || programId.trim() === '') return null;

  // The same answer as the catalog card's own expression (its non-tryout
  // branch) for every value `programs.program_type` can hold, including the
  // pass-through for a type that is neither of these — such a program keeps its
  // own segment rather than being silently filed under `class`. NOT identical
  // in one place, stated because a comment claiming parity is a claim: for a
  // MISSING or empty type the card builds an empty segment (`/programs/x//id`)
  // and this returns `class`. `DbProgramType` is a two-member union with a NOT
  // NULL column behind it, so that input does not occur — and where it did, an
  // empty segment is the worse of the two answers. The test pins both the
  // agreeing cases and this one.
  const segment = programType === 'private_lessons'
    ? 'private-lessons'
    : programType === 'class' || !programType
      ? 'class'
      : programType;
  return `${base}/${segment}/${programId}`;
}

/**
 * The public details page for one standalone tryout event:
 * `/programs/<slug>/tryouts/<eventId>`. Families read the dates, fee and venue
 * there first; its Register button leads on to
 * `/register-tryout/<slug>/<id>/register`. The catalog card and the staff
 * Share Link both call this, so the two cannot publish different addresses.
 *
 * @returns the path, or `null` when the slug or id cannot make a safe link.
 */
export function tryoutDetailPath(
  gymSlug: string | null | undefined,
  eventId: string | null | undefined
): string | null {
  const base = programCatalogPath(gymSlug);
  if (!base) return null;
  if (typeof eventId !== 'string' || !SAFE_SLUG.test(eventId.trim())) return null;
  return `${base}/tryouts/${eventId.trim()}`;
}
